import type { RoomId, UnitPositionId, UnitTypeId } from '../shared/ids';
import { serviceElement, type WithElement } from './applies-to';
import { computeGeometry, type MeasureBy, type RoomGeometry } from './geometry';
import { ownFactorsByKey } from './own-factor';
import type { Room } from './room';
import type { Shaft } from './shaft';
import type { ElementSegment, StructuralElement } from './structural-element';
import type { Opening, Wall } from './wall';

/**
 * A quantidade do serviço em cada cômodo (Fase 34, etapa 1 · D9, RN-442, RN-445).
 *
 * O avanço do item pelos cômodos é a média dos percentuais **pesada pela quantidade** de cada
 * cômodo (D9): o banheiro de 4 m² de piso pesa menos que a sala de 20. O peso é a mesma conta do
 * quantitativo, feita num cômodo só — o piso no piso, as faces no revestimento, um por cômodo na
 * contagem —, e por isso a soma dos cômodos de uma tipologia é o número que o quantitativo dá
 * para ela (`totalFor`).
 *
 * Quando o número do serviço foi corrigido na tipologia, o desenho continua dando a
 * **proporção** entre os cômodos: o peso nunca é a quantidade do item, só a parte de cada um.
 *
 * Sem banco: o caso de uso lê o desenho e chama daqui.
 */

// ── Quais serviços descem a cômodo (RN-442) ─────────────────────────────────────

/**
 * O serviço que desce a cômodo: o que mede por ambiente (`AMBIENTE`), em todos os cômodos da
 * tipologia (D6). A alvenaria, a fachada e as instalações ficam no pavimento, e a estrutura
 * desce à peça (RN-446, RN-447) — nenhum deles tem cômodo.
 */
export function descendsToRooms(service: WithElement): boolean {
  return serviceElement(service) === 'AMBIENTE';
}

// ── A medida num cômodo ─────────────────────────────────────────────────────────

/**
 * A metade por cômodo da leitura do ambiente (`LEITURA_DO_PAR.AMBIENTE`, em `geometry.ts`).
 *
 * Lá a medida lê o total da tipologia, que é a soma dos cômodos; aqui ela lê um cômodo. A
 * contagem de ambientes é um por cômodo. O teste cobra que toda medida que o ambiente aceita
 * tenha leitura aqui, e que a soma dos cômodos seja o total.
 */
const MEDIDA_NO_COMODO: Readonly<Partial<Record<MeasureBy, (room: RoomGeometry) => number>>> = {
  FLOOR: (room) => room.floorArea,
  CEILING: (room) => room.ceilingArea,
  PERIMETER: (room) => room.perimeter,
  SKIRTING: (room) => room.skirtingLength,
  COUNT: () => 1,
  FACE: (room) => room.faceArea,
  VOLUME: (room) => room.volume,
};

/**
 * Quanto do serviço o desenho põe neste cômodo. `undefined` no serviço que não desce a cômodo:
 * a parede e a peça não são de cômodo nenhum.
 */
export function roomMeasure(service: WithElement, room: RoomGeometry): number | undefined {
  if (!descendsToRooms(service)) return undefined;
  return MEDIDA_NO_COMODO[service.measureBy]?.(room);
}

// ── O fator próprio do cômodo (Fase 39, etapa 3 · RN-512) ───────────────────────

/**
 * O que a conta precisa para o fator próprio do cômodo: a medida de **todos** os cômodos da
 * tipologia — a parte de cada um sai da soma deles — e o fator próprio dos que têm, pelo id do
 * cômodo e por `ownFactorKey`.
 */
export interface RoomFactors {
  readonly geometries: readonly RoomGeometry[];
  readonly factors: Readonly<Record<string, Readonly<Record<string, number>>>>;
}

/**
 * O fator próprio dos cômodos de uma tipologia, com a conta do desenho deles. `undefined` quando
 * nenhum cômodo vivo tem fator próprio: aí nada muda, e ninguém precisa da medida cômodo a
 * cômodo.
 */
export function roomFactorsOf(
  rooms: readonly Pick<Room, 'id' | 'factors' | 'deletedAt'>[],
  geometries: readonly RoomGeometry[],
): RoomFactors | undefined {
  const factors: Record<string, Readonly<Record<string, number>>> = {};
  for (const room of rooms) {
    if (room.deletedAt !== undefined || (room.factors ?? []).length === 0) continue;
    factors[room.id] = ownFactorsByKey(room.factors);
  }
  return Object.keys(factors).length === 0 ? undefined : { geometries, factors };
}

/** Algum cômodo vivo tem fator próprio? — a pergunta barata, antes de refazer o desenho. */
export function hasRoomFactors(rooms: readonly Pick<Room, 'factors' | 'deletedAt'>[]): boolean {
  return rooms.some((room) => room.deletedAt === undefined && (room.factors ?? []).length > 0);
}

/**
 * Quanto da medida de um serviço que desce a cômodo é de cada cômodo, pelo id dele (RN-512).
 *
 * A soma dos cômodos é o medido da tipologia (`roomMeasure`, Fase 34); com a medida corrigida na
 * tipologia, cada cômodo leva a mesma proporção. Vazio no serviço que não desce a cômodo, e quando
 * nenhum cômodo mede.
 */
export function roomShares(
  service: WithElement,
  base: Pick<RoomFactors, 'geometries'>,
): Readonly<Record<string, number>> {
  if (!descendsToRooms(service)) return {};
  const medidas = base.geometries.flatMap((geometria) => {
    const medida = roomMeasure(service, geometria);
    return medida === undefined || medida <= 0 ? [] : [[geometria.roomId, medida] as const];
  });
  const total = medidas.reduce((soma, [, medida]) => soma + medida, 0);
  if (total <= 0) return {};
  return Object.fromEntries(medidas.map(([comodo, medida]) => [comodo, medida / total]));
}

// ── O desenho de uma tipologia, montado do desenho da obra ──────────────────────

/** O desenho da obra inteira, como o servidor o lê: cada lista com todas as tipologias. */
export interface ProjectDrawing {
  readonly rooms: readonly Room[];
  readonly walls: readonly Wall[];
  readonly openings: readonly Opening[];
  readonly elements: readonly StructuralElement[];
  readonly segments: readonly ElementSegment[];
  readonly shafts: readonly Shaft[];
}

/**
 * A conta de cada cômodo de uma tipologia (`RoomGeometry`), a partir do desenho da obra.
 *
 * É a parte da montagem da bancada (`geometriaDaTipologia`) que chega ao cômodo: a parede entra
 * quando um dos lados dela é cômodo desta tipologia — a interna e a divisa —, o cômodo do outro
 * lado da divisa entra como vizinho (dá lado à parede sem entrar na conta), e o vão, o pilar e a
 * viga entram pela parede em que estão. O shaft tira o piso do cômodo dele (RN-271).
 *
 * As peças da fundação, a laje e a escada ficam de fora: nenhuma medida de ambiente as soma
 * (RN-360).
 */
export function roomGeometriesOf(
  drawing: ProjectDrawing,
  unitTypeId: UnitTypeId,
): readonly RoomGeometry[] {
  const rooms = drawing.rooms.filter(
    (room) => room.unitTypeId === unitTypeId && room.deletedAt === undefined,
  );
  if (rooms.length === 0) return [];
  const meus = new Set<RoomId>(rooms.map((room) => room.id));

  const ladosDe = (wall: Wall) =>
    wall.sideB === undefined ? [wall.sideA] : [wall.sideA, wall.sideB];
  const walls = drawing.walls.filter(
    (wall) => wall.deletedAt === undefined && ladosDe(wall).some((lado) => meus.has(lado.roomId)),
  );
  const paredes = new Set<string>(walls.map((wall) => wall.id));

  const doOutroLado = new Set<RoomId>(
    walls.flatMap((wall) => ladosDe(wall).map((lado) => lado.roomId)).filter((id) => !meus.has(id)),
  );
  const segments = drawing.segments.filter((segment) => paredes.has(segment.wallId));
  const naParede = new Set<string>(segments.map((segment) => segment.elementId));

  return computeGeometry({
    rooms,
    walls,
    openings: drawing.openings.filter((opening) => paredes.has(opening.wallId)),
    neighbourRooms: drawing.rooms.filter((room) => doOutroLado.has(room.id)),
    elements: drawing.elements.filter((element) => naParede.has(element.id)),
    elementSegments: segments,
    shafts: drawing.shafts.filter((shaft) => meus.has(shaft.roomId)),
  }).rooms;
}

// ── O peso de cada cômodo do pavimento (D9) ─────────────────────────────────────

/** Um cômodo de uma posição do pavimento — o "banheiro social do 1204" (RN-441). */
export interface RoomToWeigh {
  readonly positionId: UnitPositionId;
  readonly roomId: RoomId;
  /**
   * Quantas vezes a posição existe no pavimento (`UnitPosition.count`). O apto é um; as quarenta
   * vagas iguais são uma posição com 40, e cada cômodo dela pesa quarenta vezes.
   */
  readonly times: number;
  /** A conta do desenho deste cômodo. Ausente: o cômodo não tem conta (o desenho não o achou). */
  readonly geometry?: RoomGeometry;
}

/**
 * De onde veio o peso: a quantidade do desenho em cada cômodo, ou o mesmo peso para todos — o
 * serviço sem desenho, que a tela avisa.
 */
export type RoomWeightBasis = 'DESENHO' | 'IGUAL';

export interface RoomWeight {
  readonly positionId: UnitPositionId;
  readonly roomId: RoomId;
  /** A quantidade do serviço no cômodo, vezes as vezes da posição; no `IGUAL`, as vezes. */
  readonly weight: number;
}

export interface RoomWeights {
  readonly basis: RoomWeightBasis;
  readonly weights: readonly RoomWeight[];
}

/**
 * O peso de cada cômodo no avanço do item (D9, RN-445). Recebe só os cômodos que contam: o
 * "não se aplica" sai antes (RN-444).
 *
 * O peso é a medida do serviço no cômodo (`roomMeasure`). Quando o desenho não dá número a
 * nenhum deles — o serviço sem desenho, a tipologia ainda sem contorno nem parede —, todos
 * pesam igual: o percentual de cada um vale a mesma parte, e não zero. A escolha é do conjunto,
 * e não de cada cômodo: com o desenho valendo, o cômodo que mede zero (a varanda sem parede no
 * revestimento de parede) pesa zero, porque o serviço não tem quantidade nele.
 */
export function roomWeights(service: WithElement, rooms: readonly RoomToWeigh[]): RoomWeights {
  const medidas = rooms.map((room) => {
    const medida = room.geometry === undefined ? undefined : roomMeasure(service, room.geometry);
    return medida !== undefined && medida > 0 ? medida : 0;
  });
  const total = rooms.reduce((soma, room, indice) => soma + medidas[indice]! * room.times, 0);

  if (total > 0) {
    return {
      basis: 'DESENHO',
      weights: rooms.map((room, indice) => ({
        positionId: room.positionId,
        roomId: room.roomId,
        weight: medidas[indice]! * room.times,
      })),
    };
  }
  return {
    basis: 'IGUAL',
    weights: rooms.map((room) => ({
      positionId: room.positionId,
      roomId: room.roomId,
      weight: room.times,
    })),
  };
}
